import React from 'react';
import { View, StyleSheet, Platform, useWindowDimensions } from 'react-native';
import { colors } from '../theme';
import { LanguageToggle } from './LanguageToggle';
import { ThemeToggle } from './ThemeToggle';
import { HeroVideoBackground } from './HeroVideoBackground';
import { Logo } from './Logo';
import { useLocale } from '../contexts/LocaleContext';
import { useThemedStyles } from '../hooks/useThemedStyles';

interface DualPaneLayoutProps {
  children: React.ReactNode;
  /** Screens that render their own header logo (e.g. UnifiedLoginScreen) can suppress the
   * layout's own mobile logo to avoid showing it twice. Defaults to shown. */
  showMobileLogo?: boolean;
  /** No longer rendered — the media pane is now a silent feature-reel video with no text
   * overlay. Kept as accepted-but-unused props so existing callers don't need updating. */
  title?: string;
  subtitle?: string;
}

const FEATURE_REEL_SOURCES: Record<'en' | 'ar', number> = {
  en: require('../../assets/feature-reel-60s-en.mp4'),
  ar: require('../../assets/feature-reel-60s-ar.mp4'),
};

export const DualPaneLayout: React.FC<DualPaneLayoutProps> = ({
  children,
  showMobileLogo = true,
}) => {
  const styles = useThemedStyles(getStyles);
  const { isRTL, locale } = useLocale();
  const featureReelSource = FEATURE_REEL_SOURCES[locale.split('_')[0] === 'ar' ? 'ar' : 'en'];
  // useWindowDimensions (unlike Dimensions.get, a one-time read) re-renders this component on
  // browser resize/rotation, so isMobileWeb stays live instead of freezing at whatever size
  // existed on first render.
  const { width: screenWidth } = useWindowDimensions();
  const isWeb = Platform.OS === 'web';

  if (!isWeb) {
    // Mobile: the toggle row and logo render INSIDE the same background-colored wrapper as
    // children (not as an absolutely-positioned overlay sitting above/outside it) — an
    // overlay here left a mismatched-color strip behind it, since it rendered outside
    // children's own SafeAreaView background. This mirrors the old (pre-unified) LoginScreen,
    // which never had a separate floating header pane at all.
    return (
      <View style={styles.mobileWrapper}>
        <View style={styles.mobileHeader}>
          {showMobileLogo && <Logo size="medium" />}
          <View style={styles.mobileToggle}>
            <ThemeToggle />
            <LanguageToggle />
          </View>
        </View>
        {children}
      </View>
    );
  }

  // Check if web user is on mobile device - use a smaller breakpoint
  const isMobileWeb = screenWidth <= 1024; // Increased breakpoint to catch more mobile devices

  if (isMobileWeb) {
    // Mobile web: same header row as native mobile.
    return (
      <View style={styles.mobileWrapper}>
        <View style={styles.mobileHeader}>
          {showMobileLogo && <Logo size="medium" />}
          <View style={styles.mobileToggle}>
            <ThemeToggle />
            <LanguageToggle />
          </View>
        </View>
        {children}
      </View>
    );
  }

  // Desktop web: Dual-pane layout. Both panes are plain flex children of a row container —
  // NOT position:'fixed'/absolute+margin split, which is what previously let stale right-pane
  // content visually bleed through the left pane on some browsers (RN Web's flex emulation
  // doesn't reliably reconcile a `fixed` sibling against a `marginLeft: 50%` sibling). A
  // straightforward `flexDirection: 'row'` with two `flex: 1` children has no such ambiguity.
  return (
    <View style={[styles.container, isRTL && styles.containerRTL]}>
      {/* Media Pane — sized to the video's actual 16:9 rendered width (height is the full pane,
          unconstrained), so there's never a leftover gap next to the video from `contain`.
          The form pane is flex:1 and absorbs whatever width remains. */}
      <View style={styles.leftPane}>
        <HeroVideoBackground style={styles.wireframeBackground} source={featureReelSource} />
      </View>

      {/* Form Pane */}
      <View style={styles.rightPane}>
        <View style={styles.desktopToggle}>
          <ThemeToggle />
          <LanguageToggle />
        </View>
        {children}
      </View>
    </View>
  );
};

const getStyles = () => StyleSheet.create({
  container: {
    flex: 1,
    flexDirection: 'row',
    backgroundColor: colors.background.surface,
    // A hard height (not minHeight): if rightPane's form content is taller than the viewport,
    // minHeight let `container` — and leftPane/the video with it, via flexbox cross-axis
    // stretch — grow past 100vh too. Pinning the height and letting rightPane scroll internally
    // keeps the video pane locked to the viewport regardless of form content length.
    height: Platform.OS === 'web' ? ('100vh' as any) : undefined,
  },
  containerRTL: {
    flexDirection: 'row-reverse',
  },
  leftPane: {
    // Narrower than the form pane. The feature-reel videos are portrait (1080x1920), close to
    // this column's own tall/narrow shape, so HeroVideoBackground's `cover` fills it edge-to-edge
    // with only a light top/bottom crop.
    flex: 0.7,
    position: 'relative',
    overflow: 'hidden',
  },
  wireframeBackground: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
  },
  rightPane: {
    flex: 1,
    backgroundColor: colors.background.surface,
    position: 'relative',
    zIndex: 2,
    overflow: Platform.OS === 'web' ? 'scroll' : undefined,
  },
  mobileWrapper: {
    flex: 1,
    backgroundColor: colors.background.surface,
  },
  mobileHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingTop: 48,
    paddingBottom: 8,
    paddingHorizontal: 16,
    backgroundColor: colors.background.surface,
  },
  mobileToggle: {
    flexDirection: 'row',
    gap: 8,
  },
  desktopToggle: {
    position: 'absolute',
    top: 16,
    right: 16,
    zIndex: 100,
    flexDirection: 'row',
    gap: 8,
  },
});
